import React from 'react'
import { merge } from '@/utils/nunchee-kit'
import {
  AuthButtonVariant,
  IAuthScriptOptions,
  IAuthTheme,
  IAuthLayoutTokens,
} from '@/actors/AuthActor/types'

export interface ResolvedAuthTheme {
  /**
   * CSS custom properties for the scene root. Components read them with the
   * design-system value as fallback, so an unthemed scene renders exactly as
   * it did before this existed.
   */
  vars: React.CSSProperties
  /** Page-level assets — applied as real style props / an <img> src. */
  layout: IAuthLayoutTokens
  /** Overrides the ambiance logo above the form. */
  headerImage?: string
  headerImageWidth?: number
  headerImageHeight?: number
}

export const DEFAULT_HEADER_IMAGE_WIDTH = 100
export const DEFAULT_HEADER_IMAGE_HEIGHT = 40

const BUTTON_VARIANTS: AuthButtonVariant[] = ['primary', 'secondary', 'tertiary', 'quaternary']

/**
 * Default press affordance for a themed variant. The `:active` rules chain back
 * to the scene's own token so a themed button never flashes a design-system
 * shade mid-press — which also means the pressed color equals the resting one
 * and the button reads as dead. A brightness step is derived from whatever the
 * scene set, works for the transparent variants (it dims text and border too),
 * and needs no color math the config has to supply.
 *
 * Only emitted for variants the scene actually themed: an unthemed button keeps
 * the design-system pressed shade, which already differs from its resting one.
 */
const DEFAULT_PRESS_FILTER = 'brightness(0.9)'

/**
 * `merge` mutates its target and only shallow-copies on first assignment, so a
 * nested object it assigns (`theme.button`) keeps the *store's* per-variant
 * objects by reference; the next source then deep-merges straight into the
 * cinematics store. Left unguarded, opening `login` rewrote the scene-wide
 * button colors for every other form, permanently, for the rest of the session.
 *
 * Themes are plain JSON from the cinematic, so a round-trip is a sufficient and
 * dependency-free deep clone.
 */
const cloneTheme = <T,>(value: T): T =>
  value ? (JSON.parse(JSON.stringify(value)) as T) : value

const setVar = (vars: Record<string, string>, name: string, value?: string) => {
  if (!value) return
  vars[name] = value
}

const toCssVars = (theme: IAuthTheme): React.CSSProperties => {
  const vars: Record<string, string> = {}

  setVar(vars, '--auth-surface', theme.surface)
  setVar(vars, '--auth-on-surface', theme.onSurface)
  setVar(vars, '--auth-border', theme.border)
  setVar(vars, '--auth-border-radius', theme.borderRadius)

  // Mobile-only overrides: the form card's media query reads these before the
  // desktop tokens, so a themed scene can go flat below the desktop breakpoint
  // without giving up its card on desktop.
  if (theme.flatMobile) {
    setVar(vars, '--auth-surface-mobile', 'transparent')
    setVar(vars, '--auth-border-mobile', 'none')
  }

  BUTTON_VARIANTS.forEach((variant) => {
    const tokens = theme.button?.[variant]
    if (!tokens) return

    setVar(vars, `--button-${variant}-bg`, tokens.bg)
    setVar(vars, `--button-${variant}-fg`, tokens.fg)
    setVar(vars, `--button-${variant}-border`, tokens.border)
    setVar(vars, `--button-${variant}-bg-hover`, tokens.bgHover)
    setVar(vars, `--button-${variant}-fg-hover`, tokens.fgHover)
    setVar(vars, `--button-${variant}-border-hover`, tokens.borderHover)

    setVar(vars, `--button-${variant}-bg-active`, tokens.bgActive)
    setVar(vars, `--button-${variant}-fg-active`, tokens.fgActive)
    setVar(vars, `--button-${variant}-border-active`, tokens.borderActive)

    const hasExplicitPressState = !!(tokens.bgActive || tokens.fgActive || tokens.borderActive)
    if (!hasExplicitPressState) {
      setVar(vars, `--button-${variant}-filter-active`, DEFAULT_PRESS_FILTER)
    }
  })

  setVar(vars, '--input-fg', theme.input?.fg)
  setVar(vars, '--input-border', theme.input?.border)
  setVar(vars, '--input-border-focus', theme.input?.borderFocus)
  setVar(vars, '--input-label-fg', theme.input?.label)
  setVar(vars, '--input-placeholder-fg', theme.input?.placeholder)

  return vars as React.CSSProperties
}

/**
 * Resolves the theme for the form currently on screen: the scene-wide `theme`
 * with the form's own `theme` merged over it. Scene-wide inheritance is the
 * point — styling used to be keyed per form, so switching from `main` to
 * `login` dropped the partner look mid-flow unless every form repeated itself.
 *
 * Reads from the actor's own `options` (passed down from the `script` prop)
 * rather than reaching into `scene.script[0]` — that index assumed AuthActor is
 * always the first script entry and left the embedded AuthActor (checkout,
 * redeem, my-team) unthemable.
 */
export const resolveAuthTheme = (
  options: IAuthScriptOptions | undefined,
  activeForm: string,
): ResolvedAuthTheme => {
  const formOption = options?.layout?.[activeForm]

  const theme = merge(
    {} as IAuthTheme,
    cloneTheme(options?.theme ?? {}),
    cloneTheme(formOption?.theme ?? {}),
  )

  return {
    vars: toCssVars(theme),
    layout: theme.layout ?? {},
    headerImage: theme.headerImage,
    headerImageWidth: theme.headerImageWidth ?? DEFAULT_HEADER_IMAGE_WIDTH,
    headerImageHeight: theme.headerImageHeight ?? DEFAULT_HEADER_IMAGE_HEIGHT,
  }
}
